import React, { useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import {
  ArrowLeft,
  MoreVertical,
  Users,
  FileText,
  Eye,
  TrendingUp,
  Calendar,
  DollarSign,
  CheckCircle,
  Clock,
  AlertCircle,
  MessageSquare,
  Download,
  Play
} from 'lucide-react'
import { mockCampaigns, mockDeliverables, statusColors, formatCurrency, formatNumber } from '../../data/mockData'

const tabs = ['Overview', 'Creators', 'Deliverables', 'Analytics', 'Messages']

function BrandCampaignDetail() {
  const { id } = useParams()
  const [activeTab, setActiveTab] = useState('Overview')

  const campaign = mockCampaigns.find(c => c.id === id) || mockCampaigns[0]
  const deliverables = mockDeliverables.filter(d => d.campaignId === campaign.id)
  const status = statusColors[campaign.status] || statusColors.draft

  const progress = campaign.deliverablesCount > 0
    ? Math.round((campaign.completedDeliverables / campaign.deliverablesCount) * 100)
    : 0

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-start">
          <Link to="/brand/campaigns" className="p-2 text-gray-400 hover:text-gray-600 mr-4 mt-1">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <div className="flex items-center gap-3 mb-1">
              <h1 className="text-2xl font-bold text-gray-900">{campaign.name}</h1>
              <span className={`badge ${status.bg} ${status.text}`}>{status.label}</span>
            </div>
            <p className="text-gray-500">
              {new Date(campaign.startDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })} -
              {new Date(campaign.endDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button className="btn-secondary">
            <Download className="w-4 h-4 mr-2" />
            Export Report
          </button>
          <button className="p-2 text-gray-400 hover:text-gray-600 border border-gray-200 rounded-lg">
            <MoreVertical className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="stat-card">
          <div className="flex items-center justify-between mb-2">
            <Users className="w-5 h-5 text-gray-400" />
          </div>
          <p className="text-2xl font-bold text-gray-900">{campaign.creatorsCount}</p>
          <p className="text-sm text-gray-500">Creators</p>
        </div>
        <div className="stat-card">
          <div className="flex items-center justify-between mb-2">
            <FileText className="w-5 h-5 text-gray-400" />
          </div>
          <p className="text-2xl font-bold text-gray-900">{campaign.completedDeliverables}/{campaign.deliverablesCount}</p>
          <p className="text-sm text-gray-500">Deliverables</p>
        </div>
        <div className="stat-card">
          <div className="flex items-center justify-between mb-2">
            <Eye className="w-5 h-5 text-gray-400" />
          </div>
          <p className="text-2xl font-bold text-gray-900">{formatNumber(campaign.reach)}</p>
          <p className="text-sm text-gray-500">Total Reach</p>
        </div>
        <div className="stat-card">
          <div className="flex items-center justify-between mb-2">
            <TrendingUp className="w-5 h-5 text-gray-400" />
          </div>
          <p className="text-2xl font-bold text-gray-900">{campaign.engagement}%</p>
          <p className="text-sm text-gray-500">Avg. Engagement</p>
        </div>
        <div className="stat-card">
          <div className="flex items-center justify-between mb-2">
            <DollarSign className="w-5 h-5 text-gray-400" />
          </div>
          <p className="text-2xl font-bold text-gray-900">{formatCurrency(campaign.spent)}</p>
          <p className="text-sm text-gray-500">of {formatCurrency(campaign.budget)}</p>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="card">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-medium text-gray-900">Campaign Progress</h3>
          <span className="text-sm text-gray-500">{progress}% complete</span>
        </div>
        <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-hf-teal-500 to-hf-teal-600 rounded-full transition-all"
            style={{ width: `${progress}%` }}
          />
        </div>
        <div className="flex justify-between mt-3 text-sm">
          <span className="text-gray-500">
            <CheckCircle className="w-4 h-4 inline mr-1 text-green-500" />
            {campaign.completedDeliverables} completed
          </span>
          <span className="text-gray-500">
            <Clock className="w-4 h-4 inline mr-1 text-orange-500" />
            {campaign.deliverablesCount - campaign.completedDeliverables} remaining
          </span>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex space-x-8">
          {tabs.map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === tab
                  ? 'border-hf-teal-600 text-hf-teal-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'Overview' && (
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Campaign Brief Summary */}
          <div className="lg:col-span-2 card">
            <h3 className="font-semibold text-gray-900 mb-4">Campaign Brief</h3>
            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-500 mb-1">Primary Goal</p>
                <p className="text-gray-900 font-medium capitalize">{campaign.primaryGoal}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-1">Core Message</p>
                <p className="text-gray-900">Introducing our new wellness tracking device that empowers patients with real-time health insights for better outcomes.</p>
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-1">Key Talking Points</p>
                <ul className="list-disc list-inside text-gray-900 space-y-1">
                  <li>FDA-cleared for clinical accuracy</li>
                  <li>Connects seamlessly with major EHR systems</li>
                  <li>24/7 monitoring with smart alerts</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Timeline */}
          <div className="card">
            <h3 className="font-semibold text-gray-900 mb-4">Timeline</h3>
            <div className="space-y-4">
              <div className="flex items-start">
                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">Campaign Started</p>
                  <p className="text-sm text-gray-500">Jan 15, 2026</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                  <Clock className="w-4 h-4 text-blue-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">Content Due</p>
                  <p className="text-sm text-gray-500">Feb 28, 2026</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                  <Calendar className="w-4 h-4 text-gray-400" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">Campaign End</p>
                  <p className="text-sm text-gray-500">Mar 15, 2026</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'Deliverables' && (
        <div className="card">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Creator</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Type</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Platform</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Status</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Due Date</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-gray-500">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {deliverables.map((deliverable) => {
                  const delStatus = statusColors[deliverable.status] || statusColors.pending
                  return (
                    <tr key={deliverable.id} className="hover:bg-gray-50">
                      <td className="py-4 px-4">
                        <div className="flex items-center">
                          <img
                            src={deliverable.creator.avatar}
                            alt={deliverable.creator.name}
                            className="w-8 h-8 rounded-full object-cover mr-3"
                          />
                          <span className="font-medium text-gray-900">{deliverable.creator.name}</span>
                        </div>
                      </td>
                      <td className="py-4 px-4 text-gray-600">{deliverable.type}</td>
                      <td className="py-4 px-4 text-gray-600 capitalize">{deliverable.platform}</td>
                      <td className="py-4 px-4">
                        <span className={`badge ${delStatus.bg} ${delStatus.text}`}>
                          {delStatus.label}
                        </span>
                      </td>
                      <td className="py-4 px-4 text-gray-600">
                        {new Date(deliverable.dueDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </td>
                      <td className="py-4 px-4 text-right">
                        {deliverable.status === 'brand_review' && (
                          <button className="btn-primary text-sm py-1.5">
                            Review
                          </button>
                        )}
                        {deliverable.status === 'posted' && deliverable.postUrl && (
                          <a
                            href={deliverable.postUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-hf-teal-600 hover:text-hf-teal-700 text-sm font-medium"
                          >
                            View Post
                          </a>
                        )}
                        {!['brand_review', 'posted'].includes(deliverable.status) && (
                          <button className="text-gray-400 hover:text-gray-600">
                            <MoreVertical className="w-4 h-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'Creators' && (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {deliverables.slice(0, 5).map((d, i) => (
            <div key={i} className="card">
              <div className="flex items-center space-x-3 mb-4">
                <img
                  src={d.creator.avatar}
                  alt={d.creator.name}
                  className="w-12 h-12 rounded-full object-cover"
                />
                <div>
                  <div className="flex items-center">
                    <p className="font-medium text-gray-900">{d.creator.name}</p>
                    {d.creator.verified && (
                      <CheckCircle className="w-4 h-4 text-hf-teal-600 ml-1" />
                    )}
                  </div>
                  <p className="text-sm text-gray-500">{d.creator.title}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 text-center mb-4">
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-lg font-semibold text-gray-900">{formatNumber(d.creator.totalReach)}</p>
                  <p className="text-xs text-gray-500">Reach</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-lg font-semibold text-gray-900">{d.creator.avgEngagement}%</p>
                  <p className="text-xs text-gray-500">Engagement</p>
                </div>
              </div>
              <button className="btn-secondary w-full">
                <MessageSquare className="w-4 h-4 mr-2" />
                Message
              </button>
            </div>
          ))}
        </div>
      )}

      {activeTab === 'Analytics' && (
        <div className="card text-center py-12">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <TrendingUp className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Analytics Dashboard</h3>
          <p className="text-gray-500 mb-4">Real-time performance metrics will appear here as content is posted.</p>
          <button className="btn-primary">
            View Full Analytics
          </button>
        </div>
      )}

      {activeTab === 'Messages' && (
        <div className="card text-center py-12">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <MessageSquare className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Campaign Messages</h3>
          <p className="text-gray-500 mb-4">Communicate with creators and your SOFI campaign manager here.</p>
          <button className="btn-primary">
            Start Conversation
          </button>
        </div>
      )}
    </div>
  )
}

export default BrandCampaignDetail
